import React from 'react'
import makeStyles from '@mui/styles/makeStyles'
import {
  RequirementAssesmentPayload,
  RequirementFormConfig,
} from '@shared/types'
import { useFormContext } from 'react-hook-form'
import { Button, Tooltip } from '@mui/material'
import { Box } from '@mui/material'
import { ProgressButton } from '@shared/components/progress-button.component'
import { useMutation, useQueryClient } from 'react-query'
import { apiJSON } from '@shared/utils/api/api.util'
import { useRouter } from '@shared/hooks/use-router'
import { useTemplateMutation } from './templates/use-template-mutation'
import { useACL } from '@shared/hooks/use-acl.hook'
import { useDisableDuringOngoingAudit } from '@modules/company-profile/use-disable-during-ongoing-audit.hook'
import ids from '@shared/onboarding/ids'

interface Props {
  formConfig: RequirementFormConfig
  companyID: string
  auditID: string
}

export const RequirementActions = ({
  formConfig,
  companyID,
  auditID,
}: Props) => {
  const {
    allowSave,
    allowDraft,
    prevUrl,
    invalidateQueriesUrl,
    endpointType,
    requirementID,
    requirementBaseID,
  } = formConfig
  const classes = useStyles()
  const { navigate, query } = useRouter<{ auditID?: string }>()
  const { is } = useACL()
  const queryClient = useQueryClient()

  const { shouldDisable: shouldDisableForSelectedClientRoles } =
    useDisableDuringOngoingAudit(companyID)
  const { getValues } = useFormContext()
  const { handleManageTemplates } = useTemplateMutation(
    Number(requirementBaseID)
  )
  const { mutateAsync: updateRequirement, isLoading: isUpdatingRequirement } =
    useMutation(
      async ({
        requirement,
        isDraft,
      }: {
        requirement: RequirementAssesmentPayload
        isDraft: boolean
      }) => {
        let endpoint = ''

        if (endpointType === 'client') {
          endpoint = `companies/${companyID}/publishedAuditRequirements/${requirementID}/clientAssessment`
        } else if (endpointType === 'auditor') {
          await handleManageTemplates()
          endpoint = `companies/${companyID}/audits/${auditID}/requirements/${requirementID}/auditorAssessment?isDraft=${isDraft}`
        } else if (endpointType === 'correction') {
          endpoint = `companies/${companyID}/publishedAuditRequirements/${requirementID}/assessmentCorrection`
        } else if (endpointType === 'esqulaClient') {
          endpoint = `companies/${companyID}/esqulaRequirements/${requirementID}/clientAssessment`
        } else if (endpointType === 'esqulaCorrection') {
          endpoint = `companies/${companyID}/esqulaRequirements/${requirementID}/assessmentCorrection`
        }

        return await apiJSON(endpoint, requirement, { method: 'PUT' })
      },
      {
        onMutate: async ({ requirement, isDraft }) => {
          await queryClient.cancelQueries(invalidateQueriesUrl)
          const previousData = queryClient.getQueryData(invalidateQueriesUrl)

          queryClient.setQueryData(invalidateQueriesUrl, (old: any) => {
            if (!old) return old

            const updateRequirements = (
              scopes: any[]
            ) => {
              return scopes.map((scope) => ({
                ...scope,
                groups: (scope.groups || []).map((group: any) => ({
                  ...group,
                  requirements: (group.requirements || []).map((req: any) => {
                    if (req.id === requirementID) {
                      return {
                        ...req,
                        ...requirement,
                        // Map payload fields to UI fields if necessary
                        auditorStatus: requirement.status
                          ? { statusBaseId: requirement.status }
                          : req.auditorStatus,
                        clientStatus: req.clientStatus,
                        isDraft: isDraft,
                      }
                    }
                    return req
                  }),
                })),
              }))
            }

            if (endpointType === 'client' || endpointType === 'esqulaClient' || endpointType === 'esqulaCorrection') {
              // CompanyProfile structure
              return {
                ...old,
                assessedScopes: updateRequirements(old.assessedScopes || []),
                scopesInEsqula: updateRequirements(old.scopesInEsqula || []),
              }
            } else {
              // AuditResponse structure
              return {
                ...old,
                scopedRequirements: updateRequirements(old.scopedRequirements || []),
              }
            }
          })

          return { previousData }
        },
        onError: (err, newRequirement, context: any) => {
          queryClient.setQueryData(invalidateQueriesUrl, context.previousData)
        },
        onSuccess: async () => {
          const url = new URL(prevUrl, window.location.origin)
          url.searchParams.set('focusRequirementID', String(requirementID))
          navigate(`${url.pathname}${url.search}`)
        },
        onSettled: () => {
          queryClient.invalidateQueries(invalidateQueriesUrl)
        },
      }
    )

  const handleSubmitRequirement = (isDraft: boolean) => {
    const formData = getValues()
    const payload: RequirementAssesmentPayload = { ...formData }

    if (formConfig.fields) {
      Object.entries(formConfig.fields).forEach(([fieldKey, fieldConfig]) => {
        if (fieldConfig?.saveAsKey && fieldKey in formData) {
          payload[fieldConfig.saveAsKey] =
            formData[fieldKey as keyof typeof formData]
          delete payload[fieldKey]
        }
      })
    }

    updateRequirement({ requirement: payload, isDraft })
  }

  const handleReturn = (e: React.MouseEvent) => {
    e.preventDefault()
    navigate(prevUrl, { replace: true })
  }

  return (
    <Box className={classes.buttonsContainer}>
      <Button
        variant="outlined"
        color="primary"
        onClick={handleReturn}
        id={ids.REQUIREMENT_BACK}
      >
        Powrót
      </Button>
      {allowSave && (
        <div>
          <Tooltip
            title={
              shouldDisableForSelectedClientRoles
                ? "Trwa audyt, edycja możliwa po jego zakończeniu"
                : ""
            }
            arrow
          >
            <span>
              <ProgressButton
                variant="contained"
                color="primary"
                isLoading={isUpdatingRequirement}
                onClick={() => handleSubmitRequirement(false)}
                disabled={shouldDisableForSelectedClientRoles}
              >
                Zapisz
              </ProgressButton>
            </span>
          </Tooltip>
        </div>
      )}
      {allowDraft && (
        <ProgressButton
          variant="contained"
          color="primary"
          isLoading={isUpdatingRequirement}
          onClick={() => handleSubmitRequirement(true)}
        >
          Draft
        </ProgressButton>
      )}
    </Box>
  )
}

export const useStyles = makeStyles((theme) => {
  return {
    buttonsContainer: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: theme.spacing(2),
      gap: theme.spacing(1),
    },
  }
})
